import React, { useMemo, useState, useCallback } from 'react';
import { View, StyleSheet, Text, TouchableOpacity } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ICommunityResource } from '@/types/admin/resources';
import { CommunityResource } from '@/types/residents/communityResource';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ResourceIcon as AdminResourceIcon } from '@/components/admin/resources/common/ResourceIcon';
import { getResourceItemCount } from '@/components/admin/resources/common/resourceUtils';
import { DesktopCreateResourcePanel } from '@/components/admin/resources/desktop/DesktopCreateResourcePanel';
import { DesktopEditResourcePanel } from '@/components/admin/resources/desktop/DesktopEditResourcePanel';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { ResourceGlassCard } from '@/components/shared/redesign';

export type DesktopResourceTile = {
  position: number;
  resource?: ICommunityResource;
};

interface DesktopResourcesViewProps {
  tiles: DesktopResourceTile[];
  isLoading?: boolean;
  highestPosition?: number;
  onCreateResource: () => void;
  onEditResource: (resource: ICommunityResource) => void;
  onAssignEmptySlot: (resourceId: number, position: number) => Promise<void> | void;
  onUpdateResourcePositions: (resourceId: number, position: number) => Promise<void> | void;
  selectedResourceId: number | null;
  onSelectResource: (resourceId: number | null) => void;
  onRefresh?: () => void;
}

export const DesktopResourcesView: React.FC<DesktopResourcesViewProps> = ({
  tiles,
  isLoading = false,
  highestPosition,
  onCreateResource,
  onEditResource,
  onAssignEmptySlot,
  onUpdateResourcePositions,
  selectedResourceId,
  onSelectResource,
  onRefresh,
}) => {
  const { redesign } = useAdminColors();
  const [showCreate, setShowCreate] = useState(false);
  const [editResource, setEditResource] = useState<ICommunityResource | null>(null);
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();

  const primaryTiles = useMemo(() => tiles.filter(t => t.position < 4), [tiles]);
  const secondaryTiles = useMemo(() => tiles.filter(t => t.position >= 4), [tiles]);

  const renderTile = useCallback((tile: DesktopResourceTile) => {
    if (tile.resource) {
      const resource = tile.resource;
      const totalItems = getResourceItemCount(resource);
      const isSelected = selectedResourceId === resource.id;
      const isDropTarget =
        selectedResourceId != null && selectedResourceId !== resource.id;

      const handleDropTargetPress = async () => {
        if (selectedResourceId == null || selectedResourceId === resource.id) return;
        try {
          await onUpdateResourcePositions(selectedResourceId, resource.position);
          showSuccess('Resource moved');
        } catch (err: any) {
          showApiError(err, 'Failed to move resource');
        } finally {
          onSelectResource(null);
        }
      };

      return (
        <ResourceGlassCard
          key={`res-${resource.id}`}
          resource={resource as unknown as CommunityResource}
          editMode
          showEditButton
          onPress={handleDropTargetPress}
          onEdit={() => setEditResource(resource)}
          isSelected={isSelected}
          onToggleSelect={() => onSelectResource(isSelected ? null : resource.id)}
          secondaryMeta={totalItems > 0 ? `${totalItems} items` : undefined}
          iconRenderer={(key) => (
            <AdminResourceIcon iconKey={key} size={22} color={redesign.text.primary} />
          )}
          minHeight={200}
          bodyPressable={isDropTarget}
        />
      );
    }

    // Empty slot
    return (
      <TouchableOpacity
        key={`empty-${tile.position}`}
        style={[styles.tile, styles.emptyTile, { borderColor: redesign.card.border }]}
        onPress={async () => {
          if (selectedResourceId != null) {
            try {
              await onAssignEmptySlot(selectedResourceId, tile.position);
              showSuccess('Resource moved');
            } catch (err: any) {
              showApiError(err, 'Failed to move resource');
            } finally {
              onSelectResource(null);
            }
            return;
          }
          setShowCreate(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.emptyContent}>
          <SVG_COMPONENTS.ADD_BUTTON width={20} height={20} color={redesign.text.subtle} />
          <Text style={[styles.emptyText, { color: redesign.text.subtle }]}>
            {selectedResourceId != null ? 'Move here' : 'Add resource'}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [selectedResourceId, onUpdateResourcePositions, onSelectResource, onAssignEmptySlot, showSuccess, showApiError, redesign.card.border, redesign.text.primary, redesign.text.subtle]);

  const renderPrimaryGrid = () => (
    <View style={styles.grid4Cols}>
      {Array.from({ length: 4 }).map((_, i) => {
        const tile = primaryTiles.find(t => t.position === i) || { position: i } as DesktopResourceTile;
        return (
          <View key={`p-${i}`} style={styles.gridCell}>
            {renderTile(tile)}
          </View>
        );
      })}
    </View>
  );

  const renderSecondaryGrid = () => {
    if (secondaryTiles.length === 0) return null;
    const rows: DesktopResourceTile[][] = [];
    for (let i = 0; i < secondaryTiles.length; i += 4) {
      rows.push(secondaryTiles.slice(i, i + 4));
    }
    return (
      <View style={{ gap: 8 }}>
        {rows.map((row, idx) => (
          <View key={`row-${idx}`} style={styles.grid4Cols}>
            {row.map((tile, j) => (
              <View key={`s-${idx}-${j}`} style={styles.gridCell}>
                {renderTile(tile)}
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Resources"
        subtitle="Manage community resources and their display order on the residents' app"
        showAddButton
        addButtonText="New resource"
        onAddPress={() => setShowCreate(true)}
      />

      <View style={styles.section}>
        <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>Residents dashboard</Text>
        {renderPrimaryGrid()}
      </View>

      <View style={styles.section}>
        <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>Other resources</Text>
        {renderSecondaryGrid()}
      </View>
      {showCreate && (
        <DesktopCreateResourcePanel
          visible={true}
          onClose={() => setShowCreate(false)}
          highestPosition={highestPosition ?? 0}
          onSuccess={() => {
            onRefresh?.();
          }}
        />
      )}
      {editResource && (
        <DesktopEditResourcePanel visible={true} onClose={() => setEditResource(null)} resource={editResource} />
      )}
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  grid4Cols: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    // Use horizontal padding on cells instead of gap to keep 4 per row
    marginHorizontal: -6,
  },
  gridCell: {
    flexBasis: '25%',
    maxWidth: '25%',
    paddingHorizontal: 6,
    marginBottom: 12,
  },
  tile: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    minHeight: 200,
  },
  emptyTile: {
    borderStyle: 'dashed',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContent: {
    alignItems: 'center',
    gap: 8,
  },
  emptyText: {
    fontSize: 12,
    fontWeight: '500',
  },
});


